import { catchError } from 'rxjs/operators';
import { RouterLink } from '@angular/router';
import { CommonModule, NgClass, NgForOf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { EMPTY, finalize, first, Observable } from 'rxjs';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { UserRoleEnum } from '@enums/user-role.enum';
import { NotificationService } from '@notification/service';
import { LoginService } from '../../../../service/login.service';
import { MemberService } from '../../../../service/member.service';
import { LocalStorageService } from '@services/local-storage.service';
import { ILoggedInUserViewModel } from '../../../../model/common.model';
import { IAssignedRole, IMemberClaims } from '../../../../service/memeber.model';
import { SidebarCreateSrButtonComponent } from '../sidebar-create-sr-button/sidebar-create-sr-button.component';

@Component({
  selector: 'app-sidebar-actions',
  standalone: true,
  templateUrl: './sidebar-actions.component.html',
  styleUrls: ['./sidebar-actions.component.scss'],
  imports: [
    CommonModule,
    MatRippleModule,
    MatIconModule,
    RouterLink,

    SidebarCreateSrButtonComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarActionsComponent {

  @Input() isCustomerRole = false;
  @Output() signOutClicked: EventEmitter<void> = new EventEmitter<void>();

  userDetails: ILoggedInUserViewModel = this.localStorageService.getCurrentUserDetails();

  isLoading = false;

  constructor(
    private readonly loginService: LoginService,
    private readonly memberService: MemberService,
    private readonly notificationService: NotificationService,
    private readonly localStorageService: LocalStorageService,
  ) {
  }

  /**
   * Update current user's role and associated claims, persist them in local storage,
   * redirect to the appropriate dashboard, and reload the page.
   * @param selectedRole - selected user role that we are going to set
   *
   * @remarks
   * This is refactored old `switchRole` method from header.component.ts:335. We need to think if there is better solution for this
   */
  onSwitchUserRole(selectedRole: IAssignedRole): void {
    this.isLoading = true;

    this.memberService.getMemberClaimsBasedOnRoleId(selectedRole.roleId)
      .pipe(
        first(),
        finalize((): void => {
          this.isLoading = false;
        }),
        catchError((): Observable<never> => {
          this.showRoleSwitchError();

          return EMPTY;
        })
      )
      .subscribe(
        (claims: IMemberClaims[]): void => {
          if (!claims.length) {
            this.showRoleSwitchError();

            return;
          }

          const newUserDetails = {
            ...this.userDetails,
            role: selectedRole.roleName,
            roleId: selectedRole.roleId,
            claims
          };

          this.localStorageService.setCurrentUserDetails(newUserDetails);

          this.redirectToRoleRespectiveDashboard(newUserDetails.role as UserRoleEnum).then((): void => {
            window.location.reload();
          });
        }
      );
  }

  /**
   * Redirect to the dashboard page depending on the current user role.
   * @param role - current user role
   * */
  private redirectToRoleRespectiveDashboard(role: UserRoleEnum): Promise<boolean> {
    return this.loginService.redirectToRespectiveDashboardV2(role);
  }

  /**
   * Show error notification about role switch issue
   */
  private showRoleSwitchError(): void {
    this.notificationService.error('Unable to switch the role');
  }
}
